import axios from 'axios';
import tokenService from './auth/tokenService';
import authService from './auth/authService';

const isProduction = window.location.hostname !== 'localhost';
const API_URL = isProduction ? '/api' : process.env.REACT_APP_API_URL;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable credentials for session-based auth (needed for 3D model proxy)
});

// Request interceptor to add JWT token to headers
api.interceptors.request.use(
  (config) => {
    const token = tokenService.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh on 401 errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't already tried to refresh the token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Don't try to refresh token if this is an auth endpoint
        if (originalRequest.url?.includes('/auth/')) {
          return Promise.reject(error);
        }

        // Attempt to refresh the token
        const newAccessToken = await authService.refreshToken();
        
        if (newAccessToken) {
          // Update the authorization header and retry the original request
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // If refresh fails, redirect to login
        console.error('Token refresh failed:', refreshError);
        
        // Clear auth data
        tokenService.clearAuth();
        
        // Redirect to login page
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export const organizationsApi = {
  getAll: () => api.get('/organizations'),
  getById: (id) => api.get(`/organizations/${id}`),
  create: (data) => api.post('/organizations', data),
  update: (id, data) => api.put(`/organizations/${id}`, data),
  delete: (id) => api.delete(`/organizations/${id}`),
  
  // System Admin Organization Management
  getAdminList: () => api.get('/organizations/admin/list'),
  getAdminDetails: (id) => api.get(`/organizations/admin/${id}/details`),
  createManager: (data) => api.post('/organizations/admin/create-manager', data),
  
  // Get users in organization (for Manager view)
  getUsers: (id, params) => api.get(`/organizations/${id}/users`, { params }),
};

export const networksApi = {
  getAll: () => api.get('/networks'),
  getById: (id) => api.get(`/networks/${id}`),
  create: (data) => api.post('/networks', data),
  update: (id, data) => api.put(`/networks/${id}`, data),
  delete: (id) => api.delete(`/networks/${id}`),
};

export const sectionsApi = {
  getAll: (params) => api.get('/sections', { params }), // e.g., { network_id: 1 }
  getById: (id) => api.get(`/sections/${id}`),
  // TODO: Add create, update, delete if needed
};

export const chainageApi = {
  getChainageSegments: (params) => api.get('/chainage-segments', { params }), // e.g., { section_id: 1 }
  getLocalizedChainageSegments: (params) => api.get('/localized-chainage-segments', { params }), // e.g., { chainage_segment_id: 1 }
  getChainageSegmentGeometry: (id) => api.get(`/chainage-segments/${id}/geometry`), // Get chainage segment with geometry
  getNearby: (params) => api.get('/nearby', { params }), // Get nearby chainage segments based on coordinates
  // TODO: Add create, update, delete if needed
};

export const classificationApi = {
  getCategories: () => api.get('/asset-categories'),
  getTypes: (params) => api.get('/asset-types', { params }), // e.g., { category_id: 1 }
  getSubtypes: (params) => api.get('/asset-subtypes', { params }), // e.g., { type_id: 1 }
  getDefectTypes: (params) => api.get('/defect-types', { params }), // e.g., { asset_type_id: 1, organization_id: 1 }
  getDefectTypeById: (id) => api.get(`/defect-types/${id}`), // Get a single defect type by ID
  updateSLA: (id, data) => api.put(`/defect-types/${id}/sla`, data), // Update SLA settings for a defect type
  createDefectType: (data) => api.post('/defect-types', data),
  updateDefectType: (id, data) => api.put(`/defect-types/${id}`, data),
  deleteDefectType: (id) => api.delete(`/defect-types/${id}`),
};

export const severityApi = {
  // Severity Schemes
  getSchemes: (params) => api.get('/severity-schemes', { params }), // e.g., { organization_id: 1, asset_type_id: 1 }
  getSchemeById: (id) => api.get(`/severity-schemes/${id}`),
  createScheme: (data) => api.post('/severity-schemes', data),
  updateScheme: (id, data) => api.put(`/severity-schemes/${id}`, data),
  deleteScheme: (id) => api.delete(`/severity-schemes/${id}`),
  
  // Severity Levels
  getLevels: (params) => api.get('/severity-levels', { params }), // e.g., { scheme_id: 1 }
  getLevelById: (id) => api.get(`/severity-levels/${id}`),
  createLevel: (data) => api.post('/severity-levels', data),
  updateLevel: (id, data) => api.put(`/severity-levels/${id}`, data),
  deleteLevel: (id) => api.delete(`/severity-levels/${id}`),
  
  // Get severity scheme for specific organization and asset type
  getSchemeForAsset: (params) => api.get('/severity-scheme-for-asset', { params }), // e.g., { organization_id: 1, asset_type_id: 1 }
};

export const ratingApi = {
  // Rating Schemes
  getSchemes: (params) => api.get('/ratings/schemes', { params }),
  createScheme: (data) => api.post('/ratings/schemes', data),
  updateScheme: (id, data) => api.put(`/ratings/schemes/${id}`, data),
  deleteScheme: (id) => api.delete(`/ratings/schemes/${id}`),

  // Rating Levels
  getLevels: (params) => api.get('/ratings/levels', { params }),
  createLevel: (data) => api.post('/ratings/levels', data),
  updateLevel: (id, data) => api.put(`/ratings/levels/${id}`, data),
  deleteLevel: (id) => api.delete(`/ratings/levels/${id}`),

  // Asset Rating Update
  updateRating: (id, data) => api.put(`/ratings/assets/${id}/rating`, data),
};

export const assetsApi = {
  // Example: Fetch all assets with optional query parameters
  getAll: (params) => api.get('/assets', { params }), // Pass query params
  // Example: Fetch asset by ID
  getById: (id) => api.get(`/assets/${id}`), // Includes components now
  // Example: Fetch only components for an asset
  getComponents: (assetId) => api.get(`/assets/${assetId}/components`),
  create: (data) => api.post('/assets', data),
  // Update an existing asset
  update: (id, data) => api.put(`/assets/${id}`, data),
  // Get available 3D model versions for a specific asset
  getAsset3dModelVersions: (assetId) => api.get(`/assets/${assetId}/3d-model-versions`),
  updateAssetRating: (assetId, data) => api.put(`/assets/${assetId}/rating`, data),
  // Display image selection
  setDisplayImage: (assetId, photoId) => api.put(`/assets/${assetId}/display-image`, { inspection_photo_id: photoId }),
  clearDisplayImage: (assetId) => api.put(`/assets/${assetId}/display-image`, { inspection_photo_id: null }),
  getAvailableDisplayImages: (assetId) => api.get(`/assets/${assetId}/available-display-images`),
};

export const componentsApi = {
  // Component Types
  getAllComponentTypes: () => api.get('/component-types'),
  createComponentType: (data) => api.post('/component-types', data),
  // TODO: Add update/delete for component types if needed

  // Asset Components
  getAllComponents: (params) => api.get('/components', { params }), // General fetch with filters
  getComponentById: (id) => api.get(`/components/${id}`),
  createComponent: (data) => api.post('/components', data),
  updateComponent: (id, data) => api.put(`/components/${id}`, data),
  deleteComponent: (id) => api.delete(`/components/${id}`),
};

export const usersApi = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  getInspections: (id, params) => api.get(`/users/${id}/inspections`, { params }),
};

export const envisDevicesApi = {
  getAll: (params) => api.get('/envis-devices', { params }),
  getById: (id) => api.get(`/envis-devices/${id}`),
  getByUuid: (uuid) => api.get(`/envis-devices/by-uuid/${uuid}`),
  getStats: (params) => api.get('/envis-devices/stats', { params }),
  create: (data) => api.post('/envis-devices', data),
  update: (id, data) => api.put(`/envis-devices/${id}`, data),
  delete: (id) => api.delete(`/envis-devices/${id}`),
  bind: (id, data) => api.post(`/envis-devices/${id}/bind`, data),
  unbind: (id, data) => api.post(`/envis-devices/${id}/unbind`, data),
  updateStatus: (id, data) => api.patch(`/envis-devices/${id}/status`, data),
};

export const envisFindingsApi = {
  getBoundingBoxes: (params) => api.get('/envis-findings/bounding-boxes', { params }),
  getBoundingBoxById: (id) => api.get(`/envis-findings/bounding-boxes/${id}`),
  createBoundingBox: (data) => api.post('/envis-findings/bounding-boxes', data),
  updateBoundingBox: (id, data) => api.put(`/envis-findings/bounding-boxes/${id}`, data),
  deleteBoundingBox: (id) => api.delete(`/envis-findings/bounding-boxes/${id}`),
  getDraftFindings: (params) => api.get('/envis-findings/draft-findings', { params }),
  createDraftFinding: (data) => api.post('/envis-findings/draft-findings', data),
  getDefects: (params) => api.get('/envis-findings/defects', { params }),
  getDefectById: (id) => api.get(`/envis-findings/defects/${id}`),
  createDefect: (data) => api.post('/envis-findings/defects', data),
  updateDefect: (id, data) => api.put(`/envis-findings/defects/${id}`, data),
  deleteDefect: (id) => api.delete(`/envis-findings/defects/${id}`),
  getRepeatedFindings: (params) => api.get('/envis-findings/repeated-findings', { params }),
  getRepeatedFindingsStats: (params) => api.get('/envis-findings/repeated-findings/stats', { params }),
  createRepeatedFinding: (data) => api.post('/envis-findings/repeated-findings', data),
  validateRepeatedFinding: (id, data) => api.put(`/envis-findings/repeated-findings/${id}/validate`, data),
  resolveRepeatedFinding: (id, data) => api.put(`/envis-findings/repeated-findings/${id}/resolve`, data),
  getStats: (params) => api.get('/envis-findings/stats', { params }),
  // New comprehensive dashboard statistics endpoint (server-side calculation)
  getDashboardStats: (params) => api.get('/envis-findings/dashboard-stats', { params }),
  // New unified Table Findings endpoint
  getTableFindings: (params) => api.get('/envis-findings/table-findings', { params }),
  // Burst findings endpoint
  getBurstFindings: (findingId) => api.get(`/envis-findings/${findingId}/burst-findings`),
  // Revert validated finding to draft
  revertToDraft: (id, data = {}) => api.post(`/envis-findings/bounding-boxes/${id}/revert-to-draft`, data),
  // Auto-assign preview for validation modal
  getAutoAssignPreview: (id) => api.get(`/envis-findings/bounding-boxes/${id}/auto-assign-preview`),
};

// Organization settings API
export const organizationSettingsApi = {
  get: (key) => api.get(`/organization-settings/${key}`),
  getAll: () => api.get('/organization-settings'),
  update: (key, value) => api.put(`/organization-settings/${key}`, { value }),
};

export const contractorsApi = {
  // Company management
  getCompanies: (params) => api.get('/contractors/companies', { params }),
  getCompanyById: (id) => api.get(`/contractors/companies/${id}`),
  createCompany: (data) => api.post('/contractors/companies', data),
  updateCompany: (id, data) => api.put(`/contractors/companies/${id}`, data),
  deleteCompany: (id) => api.delete(`/contractors/companies/${id}`),
  
  // Capability management
  setCapabilities: (id, data) => api.post(`/contractors/companies/${id}/capabilities`, data),
  getCapabilities: (id) => api.get(`/contractors/companies/${id}/capabilities`),
  setJurisdictions: (id, data) => api.post(`/contractors/companies/${id}/jurisdictions`, data),
  getJurisdictions: (id) => api.get(`/contractors/companies/${id}/jurisdictions`),
  
  // Staff management
  createStaff: (id, data) => api.post(`/contractors/companies/${id}/staff`, data),
  getStaff: (id) => api.get(`/contractors/companies/${id}/staff`),
  updateUser: (userId, data) => api.put(`/contractors/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/contractors/users/${userId}`),
  
  // Matching and validation
  findMatches: (data) => api.post('/contractors/find-matches', data),
  getInternalUsers: (params) => api.get('/contractors/internal-users', { params }),
};

export const inspectionTypesApi = {
  getTypes: (params) => api.get('/inspections/types', { params }),
  createType: (data) => api.post('/inspections/types', data),
  updateType: (id, data) => api.put(`/inspections/types/${id}`, data),
  deleteType: (id) => api.delete(`/inspections/types/${id}`),
  getFrequencies: () => api.get('/inspections/frequencies'),
};

export const inspectionCycleRulesApi = {
 getRules: (params) => api.get('/inspections/cycle-rules', { params }),
 createRule: (data) => api.post('/inspections/cycle-rules', data),
 updateRule: (id, data) => api.put(`/inspections/cycle-rules/${id}`, data),
 deleteRule: (id) => api.delete(`/inspections/cycle-rules/${id}`),
 getEffectiveCycle: (params) => api.get('/inspections/effective-cycle', { params }),
};

export const workOrdersApi = {
  // Work order CRUD
  getWorkOrders: (params) => api.get('/contractor-work-orders', { params }),
  getWorkOrderById: (id) => api.get(`/contractor-work-orders/${id}`),
  createWorkOrder: (data) => api.post('/contractor-work-orders', data),
  updateWorkOrder: (id, data) => api.put(`/contractor-work-orders/${id}`, data),
  deleteWorkOrder: (id) => api.delete(`/contractor-work-orders/${id}`),
  
  // Status management
  updateStatus: (id, data) => api.put(`/contractor-work-orders/${id}/status`, data),
  assignWorkOrder: (id, data) => api.put(`/contractor-work-orders/${id}/assign`, data),
  completeWorkOrder: (id, data) => {
    if (data instanceof FormData) {
      return api.put(`/contractor-work-orders/${id}/complete`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    return api.put(`/contractor-work-orders/${id}/complete`, data);
  },
  
  // Activities and documentation
  getActivities: (id) => api.get(`/contractor-work-orders/${id}/activities`),
  addActivity: (id, data) => api.post(`/contractor-work-orders/${id}/activities`, data),
  
  // Photo management
  uploadPhoto: (id, formData) => api.post(`/contractor-work-orders/${id}/photos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadPhotos: (id, formData, config = {}) => api.post(`/contractor-work-orders/${id}/photos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    ...config
  }),
  getPhotos: (id) => api.get(`/contractor-work-orders/${id}/photos`),
  addPhotos: (id, data) => api.post(`/contractor-work-orders/${id}/add-photos`, data),
  deletePhoto: (photoId) => api.delete(`/contractor-work-orders/photos/${photoId}`),
  
  // Dashboard endpoints
  getMyAssignments: (params) => api.get('/contractor-work-orders/my-assignments', { params }),
  getCompanyAssignments: (params) => api.get('/contractor-work-orders/company-assignments', { params }),
  getStatistics: (params) => api.get('/contractor-work-orders/statistics', { params }),
  
  // Enhanced analytics endpoints (Phase 3.2)
  getDetailedAnalytics: (params) => api.get('/contractor-work-orders/analytics', { params }),
  getContractorPerformance: (params) => api.get('/contractor-work-orders/contractor-performance', { params }),
  getTimeline: (params) => api.get('/contractor-work-orders/timeline', { params }),
  getDefectSummary: (params) => api.get('/contractor-work-orders/defect-summary', { params }),
  getWorkOrdersReport: (params) => api.get('/contractor-work-orders/report', { params }),
};

export const analyticsApi = {
  // Bridge analytics
  getBridgeDefects: (params) => api.get('/analytics/bridge-defects', { params }),
  getBridgeDefectDetails: (params) => api.get('/analytics/bridge-defects-details', { params }),
  getBridgeDashboardStats: (params) => api.get('/analytics/bridge-dashboard-stats', { params }),
  getBridgeDetails: (cardType, params) => api.get(`/analytics/bridge-details/${cardType}`, { params }),
  getBridgeAssetsBySection: (params) => api.get('/analytics/bridge-assets-by-section', { params }),
  getAvailableYears: () => api.get('/analytics/available-years'),
  getSlopeDashboardStats: (params) => api.get('/analytics/slope-dashboard-stats', { params }),
  getSlopeDetails: (cardType, params) => api.get(`/analytics/slope-details/${cardType}`, { params }),
};

export const organizationLayersApi = {
  // Layer management
  getLayers: (orgId) => api.get(`/organizations/${orgId}/geojson`),
  getLayerById: (orgId, layerId) => api.get(`/organizations/${orgId}/geojson/${layerId}`),
  createLayer: (orgId, formData) => api.post(`/organizations/${orgId}/geojson`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateLayer: (orgId, layerId, data) => api.put(`/organizations/${orgId}/geojson/${layerId}`, data),
  deleteLayer: (orgId, layerId) => api.delete(`/organizations/${orgId}/geojson/${layerId}`),
  
  // Secure GeoJSON proxy
  getLayerContent: (layerId) => api.get(`/geojson-proxy/${layerId}`),
  getLayerMetadata: (layerId) => api.get(`/geojson-proxy/${layerId}/metadata`),
};

export const assetDocumentsApi = {
  getAll: (assetId) => api.get(`/asset-documents/${assetId}`),
  upload: (assetId, formData) => api.post(`/asset-documents/${assetId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getDownloadUrl: (assetId, documentId) => api.get(`/asset-documents/${assetId}/${documentId}/download`),
  delete: (assetId, documentId) => api.delete(`/asset-documents/${assetId}/${documentId}`),
};

export const assetReportsApi = {
  getByAsset: (assetId) => api.get('/reports', { params: { asset_id: assetId, limit: 100 } }),
  getDownloadUrl: (reportId) => api.get(`/reports/${reportId}/download`),
};

export { default as disciplinesApi } from './disciplinesApi';

export default api; // Export the base axios instance if needed elsewhere